import React, { useRef, useState } from "react";
import Webcam from "react-webcam";
import { IconButton } from "@mui/material";
import { Button, Modal } from "react-bootstrap";
import { formatMessage } from "../../../../translations/format-message";
import toast from "react-hot-toast";
import "./camera.css";
import { FormattedMessage } from "react-intl";
import LoaderButton from "../../../../shared-components/button/loader-button";
import { MAX_IMAGE_UPLOAD_LIMIT } from "../../../../constants/constants";

interface CameraModalProps {
  uploadedImagesCount: number;
  onCapture: (images: string[]) => void;
  onClose: () => void;
}

const CameraModal: React.FC<CameraModalProps> = ({
  uploadedImagesCount,
  onCapture,
  onClose,
}) => {
  const webcamRef = useRef<Webcam | null>(null);
  const [capturedImages, setCapturedImages] = useState<string[]>([]);

  const capture = () => {
    if (uploadedImagesCount + capturedImages.length >= MAX_IMAGE_UPLOAD_LIMIT) {
      toast.error(formatMessage("upload_up_to_10_images_only"));
    } else {
      const imageSrc = webcamRef.current?.getScreenshot();
      if (imageSrc) {
        setCapturedImages((prev) => [...prev, imageSrc]);
      }
    }
  };

  const handleRemoveImage = (index: number) => {
    setCapturedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDone = () => {
    if (capturedImages.length > 0) {
      onCapture(capturedImages);
    }
    onClose();
  };

  return (
    <Modal
      className="add-user-modal correction-modal fancy-modal"
      show={true}
      centered
    >
      <Modal.Header>
        <Modal.Title>
          <FormattedMessage id="capture_image" />
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="camera-modal-body">
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/png"
          width="100%"
          videoConstraints={{ facingMode: "environment" }}
        />

        {capturedImages.length > 0 && (
          <div className="thumbnail-container">
            {capturedImages.map((img, idx) => (
              <div key={idx} className="thumbnail-wrapper">
                <img
                  src={img}
                  alt={`Captured ${idx}`}
                  className="thumbnail-image"
                />
                <IconButton
                  size="small"
                  onClick={() => handleRemoveImage(idx)}
                  className="remove-thumbnail-btn"
                >
                  <svg
                    height="18"
                    width="18"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 32 32"
                  >
                    <g>
                      <path
                        d="m16 2c-18.5.6-18.5 27.4 0 28 18.5-.6 18.5-27.4 0-28z"
                        fill="#f44336"
                      />
                      <path
                        d="m17.4 16 4.2-4.2c.9-.9-.5-2.3-1.4-1.4l-4.2 4.2-4.2-4.2c-.9-.9-2.3.5-1.4 1.4l4.2 4.2-4.2 4.2c-.9.9.5 2.3 1.4 1.4l4.2-4.2 4.2 4.2c.9.9 2.3-.5 1.4-1.4z"
                        fill="#eee"
                      />
                    </g>
                  </svg>
                </IconButton>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      <div className="modal-footer center-content">
        <LoaderButton
          type="button"
          iconName="bi-check-lg"
          onClick={capture}
          disabled={uploadedImagesCount + capturedImages.length >= 10}
          text="capture"
        />
        <LoaderButton
          type="button"
          iconName="bi-check-lg"
          onClick={handleDone}
          disabled={uploadedImagesCount + capturedImages.length === 0}
          text="button_save"
        />
        <Button
          variant="outline-danger"
          type="button"
          onClick={onClose}
          disabled={false}
        >
          <i className="bi bi-x-lg me-1"></i>
          <FormattedMessage id="close" />
        </Button>
      </div>
    </Modal>
  );
};

export default CameraModal;
